import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { NavBar } from '@alexander-nikiforov-uk/ui';
import type { NavMenuItem } from '@alexander-nikiforov-uk/ui';
import ModalAuth from '../auth/modal-auth';
import Home from '../pages/home';
import AccountMenu from './AccountMenu';
import AttentionBar from './AttentionBar';
import { LANDING_URL } from '../../constants';
import { RootStore, RootStoreContext } from '../../stores/rootStore';

const STRAPI = 'https://sapi2.desktopcallrecorder.com';

const FALLBACK_NAV_ITEMS: NavMenuItem[] = [
    { label: 'Home',          url: `${LANDING_URL}/`,                                             order: 1, isButton: false, buttonStyle: null },
    { label: 'Releases',      url: `${LANDING_URL}/releases`,                                     order: 2, isButton: false, buttonStyle: null },
    { label: 'Manual',        url: `${LANDING_URL}/manual`,                                       order: 3, isButton: false, buttonStyle: null },
    { label: 'Download',      url: 'https://download.desktopcallrecorder.com/Recorder3Setup.exe', order: 5, isButton: true,  buttonStyle: 'primary' },
];

const App = observer(() => {
    const location = useLocation();
    const { userStore, subscriptionsStore }: RootStore = useContext(RootStoreContext);
    const isLoggedIn = userStore.isLoggedIn;

    //the subscriptions list feeds the site-wide attention bar and the account-menu badge, so
    //it is loaded as soon as the customer is signed in, not only on the subscriptions page
    useEffect(() => {
        if (isLoggedIn) subscriptionsStore.fetchSubscriptionsV3();
        else subscriptionsStore.clear();
    }, [isLoggedIn]);

    const [baseNavItems, setBaseNavItems] = useState<NavMenuItem[]>(FALLBACK_NAV_ITEMS);
    const [logoUrl, setLogoUrl] = useState<string>('/logo.svg');
    const [siteName, setSiteName] = useState('Desktop Call Recorder');

    useEffect(() => {
        fetch(`${STRAPI}/api/navigation-menu?populate=menuItems`)
            .then(r => r.json())
            .then(json => {
                const data = json.data;
                const toAbsolute = (url: string) => url.startsWith('http') ? url : `${LANDING_URL}${url.startsWith('/') ? '' : '/'}${url}`;
                const items: NavMenuItem[] = [...(data.menuItems ?? [])]
                    .filter((item: NavMenuItem) => !item.url.includes('subscription.desktopcallrecorder.com'))
                    .sort((a: NavMenuItem, b: NavMenuItem) => a.order - b.order)
                    .map((item: NavMenuItem) => ({ ...item, url: toAbsolute(item.url) }));
                setBaseNavItems(items);
                setSiteName(data.logoText ?? 'Desktop Call Recorder');
                if (data.logo?.url) {
                    setLogoUrl(data.logo.url.startsWith('http') ? data.logo.url : `${STRAPI}${data.logo.url}`);
                }
            })
            .catch(() => {});
    }, []);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavBar
                menuItems={baseNavItems}
                logoText={siteName}
                logoUrl={logoUrl}
                currentPath={location.pathname}
                homeUrl={`${LANDING_URL}/`}
                rightSlot={<AccountMenu />}
                variant="compact"
            />
            <AttentionBar />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {location.pathname === '/' ? <Home /> : <Outlet />}
                <ModalAuth />
            </div>
            <footer style={{ borderTop: '1px solid var(--border)', padding: '24px 0', background: 'var(--bg)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 'var(--max)', margin: '0 auto', padding: '0 44px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <img src={logoUrl} alt="" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
                        <span style={{ fontSize: '.76rem', color: 'var(--muted)' }}>© {new Date().getFullYear()} Domit LTD · {siteName}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', justifyContent: 'flex-end' }}>
                        {[
                            { label: 'Home',           url: `${LANDING_URL}/` },
                            { label: 'Manual',         url: `${LANDING_URL}/manual` },
                            { label: 'Terms',          url: `${LANDING_URL}/terms` },
                            { label: 'Privacy Policy', url: `${LANDING_URL}/privacy-policy` },
                            { label: 'Refund Policy',  url: `${LANDING_URL}/refund-policy` },
                            { label: 'Contact',        url: 'mailto:team@desktopcallrecorder.com' },
                        ].map(link => (
                            <a key={link.label} href={link.url} style={{ fontSize: '.76rem', color: 'var(--muted)' }}
                               onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
                               onMouseLeave={e => (e.currentTarget.style.color = 'var(--muted)')}>
                                {link.label}
                            </a>
                        ))}
                    </div>
                </div>
            </footer>
        </div>
    );
});

export default App;
